import {
  Button,
  DateInput,
  FileInputControlled,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { Control, UseFormWatch } from 'react-hook-form';
import { Col, Row } from 'reactstrap';
import { FormValues } from '../../types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useValidations } from '../../../../../../hooks/useValidations';

interface FormFieldsProps {
  academicLevels: SelectOptionType[];
  countries: SelectOptionType[];
  institutions: SelectOptionType[];
  control: Control<FormValues>;
  isSubmitting: boolean;
  closeModal: () => void;
  handleDownloadFile: (key: string) => void;
  watch: UseFormWatch<FormValues>;
}

export const FormFields = ({
  academicLevels,
  countries,
  institutions,
  control,
  isSubmitting,
  closeModal,
  handleDownloadFile,
  watch,
}: FormFieldsProps) => {
  const { t } = useTranslation();
  const { msgValidations } = useValidations();

  const texts = useMemo(() => {
    const prefix = 'studentManagement';

    return {
      academicLevel: t(`${prefix}.educationalInformation.form.academicLevel`),
      country: t(`${prefix}.educationalInformation.form.country`),
      institutionId: t(`${prefix}.educationalInformation.form.institutionId`),
      validatedTitle: t(`${prefix}.educationalInformation.form.validatedTitle`),
      program: t(`${prefix}.educationalInformation.form.program`),
      institution: t(`${prefix}.educationalInformation.form.institution`),
      graduationDate: t(`${prefix}.educationalInformation.form.graduationDate`),
      description: t(`${prefix}.educationalInformation.form.description`),
      certificate: t(`${prefix}.educationalInformation.form.certificate`),
      gradeAct: t(`${prefix}.educationalInformation.form.gradeAct`),
      cancel: t(`${prefix}.educationalInformation.form.cancel`),
      submit: t(`${prefix}.educationalInformation.form.submit`),
    };
  }, [t]);

  const [description] = watch(['description']);

  return (
    <Row xs={12}>
      <Col xs={6}>
        <Select
          label={texts.academicLevel}
          options={academicLevels}
          control={control}
          name="academicLevelId"
          disabled={isSubmitting}
          rules={{
            required: msgValidations.required,
          }}
        />
        <Select
          label={texts.country}
          options={countries}
          control={control}
          name="country"
          disabled={isSubmitting}
          rules={{
            required: msgValidations.required,
          }}
        />
        <Select
          label={texts.validatedTitle}
          options={[
            { label: 'Si', value: 1 },
            { label: 'No', value: 0 },
          ]}
          control={control}
          name="validated"
          disabled={isSubmitting}
          rules={{
            required: msgValidations.required,
          }}
        />
      </Col>
      <Col xs={6}>
        <TextInput
          label={texts.program}
          control={control}
          name="programName"
          disabled={isSubmitting}
          rules={{
            required: msgValidations.required,
          }}
        />
        <Select
          label={texts.institution}
          options={institutions}
          control={control}
          name="institution"
          disabled={isSubmitting}
          rules={{
            required: msgValidations.required,
          }}
        />
        <DateInput
          label={texts.graduationDate}
          control={control}
          name="graduationDate"
          disabled={isSubmitting}
          maxDate={new Date()}
          rules={{
            required: msgValidations.required,
          }}
        />
      </Col>
      <Col xs={12}>
        <TextAreaInput
          name="description"
          control={control}
          label={texts.description}
          disabled={isSubmitting}
          rules={{
            maxLength: {
              value: 50,
              message: t('common.validations.maxLength', {
                length: 50,
              }),
            },
          }}
        />
        <span>{`${description?.length || 0}/50`}</span>
      </Col>
      <Col xs={12} className="mt-2">
        <FileInputControlled
          name="certificate"
          label={texts.certificate}
          btnText={'CARGAR'}
          accept="image/png, image/jpeg, application/pdf"
          control={control}
          onDownloadFile={() => handleDownloadFile('certificate')}
          disabled={isSubmitting}
        />
      </Col>
      <Col xs={12} className="mt-2">
        <FileInputControlled
          name="gradeAct"
          label={texts.gradeAct}
          btnText={'CARGAR'}
          accept="image/png, image/jpeg, application/pdf"
          control={control}
          onDownloadFile={() => handleDownloadFile('grade_act')}
          rules={{
            required: msgValidations.required,
          }}
          disabled={isSubmitting}
        />
      </Col>
      <Col xs={12} className="mt-4">
        <Row xs={12}>
          <Col xs={6}>
            <Button
              text={texts.cancel}
              outlined
              onClick={closeModal}
              size="lg"
              fullwidth
              disabled={isSubmitting}
            />
          </Col>
          <Col xs={6}>
            <Button
              text={texts.submit}
              type="submit"
              size="lg"
              fullwidth
              loading={isSubmitting}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
